import { Injectable } from '@angular/core';
import { QueryParams } from '../query-params/query-params.class';
import { QueryParamsService } from '../query-params/query-params.service';

@Injectable({
  providedIn: 'root',
})
export class ColorService {
  private DEFAULT_PRIMARY_COLOR: string = 'DB305E';
  private DEFAULT_SECONDARY_COLOR: string = '0A1018';
  private DEFAULT_BACKROUND_COLOR: string = 'F8F9FB';
  private primaryColor: string;
  private secondaryColor: string;
  private backgroundColor: string;

  constructor(private queryParamService: QueryParamsService) {}

  public setPrimaryColor(color: string): void {
    this.primaryColor = color;
  }

  public getPrimaryColor(): string {
    return this.primaryColor;
  }

  public setSecondaryColor(color: string): void {
    this.secondaryColor = color;
  }

  public getSecondaryColor(): string {
    return this.secondaryColor;
  }

  public getBackgroundColor(): string {
    return this.backgroundColor;
  }

  public setBackgroundColor(color: string): void {
    this.backgroundColor = color;
  }

  public setDynamicStyles(params: QueryParams): void {
    const id = params.id;
    let primaryColor, secondaryColor, backgroundColor;

    const storedColorsRaw = localStorage.getItem(`${id}-color`);
    const storedColors = storedColorsRaw ? JSON.parse(storedColorsRaw) : {};

    const colors = ['primaryColor', 'secondaryColor', 'backgroundColor'];

    const dynamicColors: Record<string, string | null> = {};

    colors.forEach((key) => {
      const cleandParam = this.removeHash(params[key]);
      if (this.isHexColorWithoutHash(cleandParam)) {
        dynamicColors[key] = cleandParam;
        this.removeHashFromUrlParam(key, cleandParam);
      } else {
        dynamicColors[key] = storedColors[key] || null;
      }
    });

    //use parameters first if possible
    primaryColor = dynamicColors.primaryColor ?? this.DEFAULT_PRIMARY_COLOR;
    secondaryColor = dynamicColors.secondaryColor ?? this.DEFAULT_SECONDARY_COLOR;
    backgroundColor = dynamicColors.backgroundColor ?? this.DEFAULT_BACKROUND_COLOR;

    localStorage.setItem(
      `${id}-color`,
      JSON.stringify({
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        backgroundColor: backgroundColor,
      })
    );

    const primaryColorCss = '#' + primaryColor;
    const secondaryColorCss = '#' + secondaryColor;
    const backgroundColorCss = '#' + backgroundColor;

    this.setPrimaryColor(primaryColorCss);
    this.setSecondaryColor(secondaryColorCss);
    this.setBackgroundColor(backgroundColorCss);

    const style: HTMLStyleElement = document.createElement('style');
    document.head.appendChild(style);

    style.sheet.insertRule(
      `:root {
        --primary-color: ${primaryColorCss};
        --secondary-color: ${secondaryColorCss};
        --background-color: ${backgroundColorCss};
      }`
    );
    style.sheet.insertRule(
      '.dynamic-color-hover-background {background-color: ' +
        primaryColorCss +
        ';}'
    );
    style.sheet.insertRule(
      '.dynamic-color-hover-background:hover {background-color: ' +
        secondaryColorCss +
        ';}'
    );

    style.sheet.insertRule(
      '.dynamic-color-primary-background {background-color: ' +
        primaryColorCss +
        ';}'
    );
    style.sheet.insertRule(
      '.dynamic-color-primary-fill {fill:' + primaryColorCss + ';}'
    );
    style.sheet.insertRule(
      '.dynamic-color-secondary-border {border-color:' +
        secondaryColorCss +
        ';}'
    );
  }

  private isHexColorWithoutHash(str: string): boolean {
    return /^(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(
      str
    );
  }

  private removeHash(value: string) {
    if (value != null && !(value.includes('#') || value.includes('%23'))) {
      return value;
    }
    return decodeURIComponent(value).replace(/#/g, '');
  }

  // Need to remove all "#". When redirect from swish/bankid we lose param value when returning to browser
  private removeHashFromUrlParam(paramKey: string, value: string) {
    try {
      const cleandValue = this.removeHash(value);
      this.queryParamService.setQueryParam(paramKey, cleandValue);
      return true;
    }
    catch {
      return false;
    }
  }
}
